import { STORY_SOURCE, story } from '@/content/storyBuilder'

// Written from the five-part full-page capture of digitopia.co/customers/brisa/
// sent on 2026-08-29 — "ilgili sayfayı tıpatıp tastamam şekilde hazırla" —
// together with the wide photograph of Brisa's R&D centre, supplied separately
// at 1200×455 because the one inside the capture is a screenshot of it.
//
// The reference's shape, which is not the template's usual one: the film
// already sat in the header, a single centred quotation stands between the
// header and the picture, and everything after the picture is an interview —
// one company section and five questions, each a heading with the manager
// answering under it in the first person. No grey panels: the reference runs
// the whole story on the page, so no section carries `band`. Filiz Mert's
// portrait, mark and attribution have been in `content/testimonials.ts` keyed
// to this slug since the Hall of Fame shipped, so the card at the foot needs
// nothing here but `closingQuote` — the reference closes on a longer wording
// of the same passage than the Hall of Fame carries, spelling out
// "DMI(Digital Maturity Index)" where the card says "digital transformation
// solution".
export const added = story('brisa', 'Brisa', {
  doc: 'bc294844-2a1e-41c3-94e5-ff08f395fe7f-BriSA and Digitopia.pdf',
  video: 'ZX4myCXII8Y',
  lede: ['“We are in heavy seas, and Digital Maturity Assessment is a lighthouse.”'],
  intro:
    '“Digitopia’s service offers us a framework, even though it is actually a self-assessment, '
    + 'it provides us with calibration support from an outside, impartial perspective, developed '
    + 'and customized specifically for our company.”',
  photo: {
    src: '/photo/brisa-rd-centre-building.webp',
    alt: 'The corner of Brisa’s stone-clad R&D centre under a pale sky, the BRISA AR|GE Merkezi '
      + 'sign on its face, with green shrubs across the foreground.',
  },
  sections: [
    {
      heading: 'About Brisa',
      body: [
        'Brisa’s foundations were laid with Lassa, which was founded in 1974 on the initiative '
        + 'of Sabancı Holding and its partners. In parallel with the developments in the tire '
        + 'industry globally; Brisa adopted its current name in 1988 as a result of the '
        + 'partnership between Sabancı Group and Bridgestone Corporation, the world’s largest '
        + 'tire manufacturer. Today, it is the leading player in the Turkish tire market.',
        'Brisa’s Kocaeli Production facilities are one of the largest single-building tire '
        + 'factories in the world. The Aksaray factory, on the other hand, is the industry’s '
        + 'first factory equipped with smart applications. Lassa branded products produced in '
        + 'these factories are found in vehicles all over the world, with 80 distributors in '
        + 'more than 80 countries and 610 stores with signage.',
        'In its bid to provide for the Turkish economy as well as care for the social '
        + 'development of the country, Brisa approaches its sustainability activities in a way '
        + 'that contributes to the Sustainable Development Goals of the United Nations.',
        'Some of Brisa’s foremost initiatives include “Let Cranes Always Fly”, where it '
        + 'contributes to biodiversity, “Brisa Academy” and “Brisa Members Education Support '
        + 'Association”, where it contributes to education, and “Brisa Museum”, which adds a '
        + 'cultural flavour. The company is among the leaders in the fields of “Climate Change” '
        + 'and “Water Security” in CDP Turkey and is on the global leaders list in “Supplier '
        + 'Relations Rating”. Announcing a 56% reduction in direct emissions by 2030 and a net '
        + 'zero emissions target by 2050, Brisa became the first company to have its long-term '
        + 'emission reduction targets set according to the 1.5 degree scenario approved by the '
        + 'Science Based Targets Initiative.',
        'Brisa, Bridgestone, Lassa, Dayton, Kinesis, Firestone tire brands, Bandag coating '
        + 'brand, Lastigim, Otopratik and Propratik service points with their main brands, '
        + 'Lastik.com.tr internet sales site, Mobilfix mobile maintenance service that provides '
        + 'on-site maintenance to commercial vehicle fleets and Profleet service package, Probox '
        + 'mobile sales and service point and Tire Hotel tire storage services all contribute to '
        + 'the Turkish economy and its development. In the area of sustainable mobility, the '
        + 'company partners with Arvento Mobil Systems to add value to its fleet customers by '
        + 'bringing together tire, service and telematics business lines.',
      ],
    },
    {
      heading: 'How did you decide to adopt a DMI service?',
      body: [
        'Our digital transformation journey started with a vision announced by Sabancı Holding '
        + 'in 2018. A comprehensive survey was conducted with middle group managers of all group '
        + 'companies, aiming to understand how prepared the companies were for this journey in '
        + 'terms of infrastructure, human resources and databases. We learned our position among '
        + 'the group companies by getting certain scores with a question set similar to the one '
        + 'offered by DMI, and as a result, we prepared our Digital Transformation roadmap. '
        + 'Since 2018, we have implemented a number of works in accordance with our roadmap and '
        + 'we continue to do so. However, while making these changes during our digital journey, '
        + 'a need arose to measure our steps by framing them. Because the journey ahead is long '
        + 'and the definition of digitalization in 2018 was very different to what it is today. '
        + 'The company’s maturity levels also evolve over time. When we searched for companies '
        + 'that deliver the services required both on a global scale and in Turkey, we noticed '
        + 'that there are not many companies in this field that operate with the assistance of a '
        + 'tool, and the tools commonly available are not suitable for the sector. Afterwards, '
        + 'we met with Mr. Halil, and we started our partnership by choosing the solution '
        + 'developed by Digitopia in terms of both industry-specific suitability and the '
        + 'opportunity to tailor the services to our company.',
        'We completed our first study by holding sessions in 6 categories with the participation '
        + 'of approximately 130 employees in total.',
      ],
    },
    {
      heading:
        'What were the benefits of having received a consultancy service to learn your DMI '
        + 'score for Brisa?',
      body: [
        'Today, when it comes to digital transformation, the technology dimension is what '
        + 'springs to mind, but it also offers the customer dimension, operations and '
        + 'management. Thanks to DMI, without neglecting these areas in our digital '
        + 'transformation journey, we have seen where we are positioned and how we can further '
        + 'our progress towards our goals with a more comprehensive evaluation.',
        'One of the benefits of the consultancy service was calibration. DMI has given us an '
        + 'advantage in terms of meeting different departments on common ground and progressing '
        + 'in line with one another. We can say that DMI measurement is a self-assessment that '
        + 'represents the whole company and is adopted by the whole company, creating a '
        + 'collective awareness.',
        'In addition to these benefits, Digitopia’s service offers us a framework, even though '
        + 'it is actually a self-assessment, it provides us with calibration support from an '
        + 'outside, impartial perspective, developed and customized specifically for our '
        + 'company. These are the greatest benefits of DMI for us. DMI offers a structure that '
        + 'is both sufficiently customized and flexible and comprehensive enough to make global '
        + 'comparisons.',
      ],
    },
    {
      heading: 'What would be different for you if such a study had never been carried out?',
      body: [
        'At Brisa, we express our company strategy through the metaphor of a sailboat. For the '
        + 'last two years, we seem to be sailing on rough seas. The sea is so rough that it is '
        + 'difficult to think about moving forward, when it is difficult to even survive. '
        + 'However, waves present not only a risk but also an opportunity, if properly '
        + 'evaluated. If you navigate the wave correctly, it will accelerate you forward. We '
        + 'consider the digital transformation as the wind that is driving this sailboat '
        + 'forward. And the Digital Maturity Assessment is a lighthouse that guides us through '
        + 'these heavy seas. We needed a beacon that would guide us and ensure our journey '
        + 'remained safe: a stable and constant guide amid the changing world, customer '
        + 'behaviour and competitor dynamics. Therefore, yes, without DMI, we would have managed '
        + 'to keep moving somehow, but thanks to this measurement, we can better see the '
        + 'opportunities and risks, set the right priorities, and prevent erroneous investments.',
      ],
    },
    {
      heading: 'Are you considering renewing the DMI service? If so, why?',
      body: [
        'We have been renewing the DMI service on a regular basis for 3 years. This year, we '
        + 'plan to include more DMI results in our action plan by pulling this study earlier in '
        + 'order to evaluate DMI outputs and determine company strategies according to these '
        + 'outputs at our strategy meeting. We will continue to renew DMI to keep seeing where '
        + 'our strengths lie and also the muscles we need to strengthen, and what we have '
        + 'achieved in the last 3 years in our digital transformation journey.',
        'In which dimensions have you seen more progress in these 3 years, and in which ones do '
        + 'you think you fell behind?',
        'Where we have made the most progress in the last 3 years has been the Innovation '
        + 'dimension. Innovation at Brisa is managed by a separate team. This team operates in a '
        + 'variety of ways both internally and externally, such as developing new ideas within '
        + 'the company, rewarding and implementing these ideas, following up with routine '
        + 'meetings, as well as establishing collaborations with venture firms. In 2019, we '
        + 'received first prize in the Innovation Organization and Culture category at the '
        + 'Inovalig Innovation Leaders Award Ceremony of the Turkish Exporters’ Association. '
        + 'Therefore, we can say that the innovation category is one of the fastest growing '
        + 'dimensions as a result of taking very concrete steps.',
        'Technology is also developing rapidly. We can exemplify this rise by implementing the '
        + 'Carbon Data Lake project and taking steps on the side of AI and RPA. According to the '
        + 'last report you shared, we see that we are the leader in the sector with the points '
        + 'we have achieved.',
        'We are also making positive progress in the Human dimension. Before the pandemic, we '
        + 'made an infrastructure change regarding remote working compatibility, which allowed '
        + 'us to quickly adapt to the new way of the world when the pandemic hit. We recently '
        + 'received an award from IDC for the BriWhere application we developed to facilitate '
        + 'remote working.',
        'We are currently working on the new working model within the scope of the Future of '
        + 'Work project, which we designed with an agile working system with more than 90 Brisa '
        + 'employees in 7 different dimensions. With this initiative, which allows us to '
        + 'consider different aspects from the mental health of our employees to the hybrid '
        + 'working environment, from the competencies of the new period to the adoption of new '
        + 'management approaches, we are reconstructing the way we work. As a result of these '
        + 'concrete steps, we hope to see big leaps in the Human dimension in the next '
        + 'measurement.',
        'On the other hand, although we are seeing a positive trend in the Customer dimension, '
        + 'we can say that this is the dimension where we need to see the biggest improvement '
        + 'since we are not a retail company. At the end of all these evaluations, I can say '
        + 'that Brisa is the leader in the sector with its current digital maturity score.',
      ],
    },
    {
      heading: 'Is DMI a KPI for your company?',
      body: [
        'Yes, DMI is a KPI that we follow, and our DMI score serves as a framework in order to '
        + 'evaluate and compare all actions for digital transformation. I can say that it is a '
        + 'KPI that we use while shaping our digital transformation roadmap.',
      ],
    },
  ],
  closingQuote: [
    'We have been renewing the DMI(Digital Maturity Index) service on a regular basis for 3 '
    + 'years. This year, we plan to include more DMI results in our action plan by pulling this '
    + 'study earlier in order to evaluate DMI outputs and determine company strategies according '
    + 'to these outputs at our strategy meeting. We will continue to renew DMI to keep seeing '
    + 'where our strengths lie and also the muscles we need to strengthen, and what we have '
    + 'achieved in the last 3 years in our digital transformation journey.',
  ],
  check:
    'This page’s words were transcribed from a five-part full-page capture of '
    + `${STORY_SOURCE}/brisa/ supplied on 2026-08-29, not from a file we hold, so please `
    + 'proofread it against the source before production — particularly the figures in “About '
    + 'Brisa” (80 distributors in more than 80 countries, 610 stores, a 56% emissions '
    + 'reduction by 2030, net zero by 2050) and the “approximately 130 employees” and “6 '
    + 'categories” of the first study. Two things are the reference’s own shape rather than '
    + 'mistakes here, and both are worth a decision: the whole interview is the manager '
    + 'speaking in the first person with no attribution until the quote card at the foot, and '
    + '“In which dimensions have you seen more progress in these 3 years…” is plainly the '
    + 'interviewer’s question but is set as a body paragraph on the live page, so it reads as '
    + 'the speaker asking herself. The reference also dates itself — “for the last two years” '
    + 'and the pandemic in the present tense, an award from 2019, a three-year renewal cycle — '
    + 'so somebody should confirm the page is still meant to read as of its writing. Three '
    + 'deliberate differences from the reference, each a template-wide decision rather than '
    + 'this page’s: the header eyebrow reads “Digitopia / Customer Success Story” where '
    + 'digitopia.co reads “SUCCESS STORIES”; the “Read the full story (PDF)” button sits in '
    + 'the header here and at the foot there; and the standfirst quotation is drawn in the '
    + 'template’s grey band, where the reference runs it on white.',
})

/** Its place in the hand-placed order the imported Customers page set. */
export const order = 7
